import React from 'react'
import AccordionMui from '@mui/material/Accordion'
import AccordionDetailsMui from '@mui/material/AccordionDetails'
import AccordionSummaryMui from '@mui/material/AccordionSummary'
import CycleHeader from '../cycle-header/CycleHeader'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { Cycle as CycleEntity } from '../../entities/cycle/Cycle'
import Cycle from '../cycle/Cycle'
import SimpleCycle from '../simple-cycle/SimpleCycle'

import { styled } from '@mui/material/styles'
import CircularProgressMui from '@mui/material/CircularProgress'
import Step from '../../constants/Step'
import CountdownPickupTimer from '../countdown-pickup-timer/CountdownPickupTimerContainer'

export interface IOwnProps {
  title: string
  cycles: CycleEntity[]
  productsCount: number
  className?: string
}

export interface IStateProps {
  isRequesting: boolean
  openCycles: Partial<Record<Step, string>>
}

export interface IDispatchProps {
  setOpenCycle: (step:number, cycleCode: string) => void
}

export type IProps = IOwnProps & IStateProps & IDispatchProps

const Accordion = styled(AccordionMui)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'stretch',
  alignItems: 'stretch',
  backgroundColor: theme.palette.whiteColor.main,
  color: theme.palette.blackColor.main,
  overflow: 'hidden',
  boxShadow: '0 -1px 0 0 rgba(212, 212, 211, 0.44)',
  '&:last-of-type': {
    borderRadius: 0,
  },
  '&.Mui-expanded': {
    flexGrow: 1,
  },
  '& .MuiCollapse-root': {
    flexGrow: 1,
    display: 'grid',
  },
  '& .MuiAccordion-region': {
    height: '100%',
    position: 'relative',
  },
}))

const AccordionSummary = styled(AccordionSummaryMui)({
  padding: 0,
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'none',
  },
  '& .MuiAccordionSummary-content': {
    display: 'block',
    margin: '0 !important',
    '&.Mui-expanded': {
      cursor: 'default',
      borderBottom: '1px solid rgba(127, 127, 127, 0.44)',
    },
  },
  '&.Mui-expanded': {
    margin: 0,
  },
})

const AccordionSummaryIcon = styled(KeyboardArrowDownIcon)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  right: theme.spacing(2),
  transform: 'translateY(-50%)',
  fontSize: 36,
  color: theme.palette.blackColor.light,
}))

const AccordionDetails = styled(AccordionDetailsMui)({
  padding: '25px 10px',
  boxShadow: 'inset 0px 0px 7px 1px rgb(127 127 127 / 35%)',
  height: '100%',
  boxSizing: 'border-box',
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
})

const TitleContainer = styled('h3')({
  fontSize: 16,
  color: '#d4d4d3',
  fontWeight: 400,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '& span': {
    fontWeight: 700,
  },
})

const Title = styled('div')({
  position: 'relative',
})

const SimpleCycleItem = styled('div')({
  marginBottom: 20,
})

const CircularProgress = styled(CircularProgressMui)({
  position: 'absolute',
  top: 2,
  right: -20,
})

const getFirstCycleWithOrders = (cycles: CycleEntity[]): CycleEntity | undefined => {
  return cycles.find(cycle => cycle.orders.length)
}

const OrdersColumn: React.FC<IProps> = (props) => {
  const {
    title,
    cycles = [],
    productsCount,
    className,
    isRequesting,
    openCycles,
    setOpenCycle,
  } = props

  const firstCycleWithOrders = React.useMemo(() => getFirstCycleWithOrders(cycles), [cycles])

  React.useEffect(() => {
    if (firstCycleWithOrders && !openCycles[firstCycleWithOrders.column.step]) {
      setOpenCycle(firstCycleWithOrders.column.step, firstCycleWithOrders.code)
    }
  }, [firstCycleWithOrders, setOpenCycle, openCycles])

  const handleChange = (cycleCode: string, step?: Step) => () => {
      if (step === undefined) {
        return
      }

      setOpenCycle(step, cycleCode)
    }

  const isExpanded = (cycle: CycleEntity) => openCycles[cycle.column.step] === cycle.code
  const isReadyColumn = cycles.every(cycle => cycle.isReady)

  return (
    <div className={className}>
      <TitleContainer>
        <Title>
          {title} <span>{productsCount}</span>
          {isRequesting ? <CircularProgress size={16}/> : null}
        </Title>
      </TitleContainer>

      {isReadyColumn ? <CountdownPickupTimer /> : null}

      {
        cycles.map(cycle => cycle.isReady ? (
          <SimpleCycleItem key={cycle.code}>
            <SimpleCycle cycle={cycle} />
          </SimpleCycleItem>
        ) : (
          <Accordion
            expanded={isExpanded(cycle)}
            onChange={!isExpanded(cycle) ? handleChange(cycle.code, cycle.column.step) : undefined}
            key={cycle.code}
            disableGutters
          >
            <AccordionSummary
              expandIcon={!isExpanded(cycle) ? <AccordionSummaryIcon /> : null}
              aria-controls='panel1a-content'
              id='panel1a-header'
            >
              <CycleHeader
                key={cycle.pickupFrom.toString()}
                active={isExpanded(cycle)}
                cycle={cycle}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Cycle cycle={cycle} />
            </AccordionDetails>
          </Accordion>
        ))
      }
    </div>
  )
}

export default styled(OrdersColumn)({
  display: 'flex',
  flexDirection: 'column',
})
